import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
	Wrapper,
	HeroSection,
	HeroImage,
	GroomName,
	BrideName,
	AndSymbol,
	HeroDate,
	CountdownSection,
	CountdownTitle,
	IntroSection,
	QuranText,
	MainIntro,
	SubIntro,
	CoupleImages,
	CoupleLabel,
	QuranSection,
	Bismillah,
	Verse,
	Ending,
	LoveStorySection,
	SectionTitle,
	StoryList,
	StoryItem,
	StoryItemTitle,
	StoryItemContent,
	GallerySection,
	CommentsSection,
	CommentTitle,
	CommentDesc,
	LocationSection,
	LocationName,
	LocationBtn,
	Credit,
	Divider,
	FloralTop,
	FloralBottom,
	GoldLine
} from "./styles";

import {
	IGallery,
	INVITATION_OCCASION,
	INVITATION_TYPES,
	Invitations,
	LangType
} from "../../../../store/services/invitations/dto/invitations.dto";
import { TemplateColorObject } from "../../../../InvitationColorMatrix";
import { getFieldMap } from "../../../../utils/inputs";
import { LANG_AR, LANG_EN, RTL } from "../constants";
import langEN from "../lang.EN.json";
import langAR from "../lang.AR.json";

import { getWeddingDate } from "../../../../utils/formatDate";
import SaveTheDateBtn from "../Components/SaveTheDateBtn";
import CountDown from "../CountDown";
import InvitationGallery from "../../../InvitationGallery";
import Comments from "../Components/Comments";
import MapComponent from "../Components/MapComponent/MapComponent";
import MapButton from "../Components/MapButton";
import PartnersSocialList from "../Components/PartnersSocialList";
import Footer from "../Components/Footer";
const Template05 = forwardRef(
	(
		{
			data,
			gallery,
			colorMatrix,
			canEdit = false
		}: {
			data: Invitations;
			gallery: IGallery[];
			colorMatrix: TemplateColorObject;
			canEdit?: boolean;
			setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
		},
		ref
	) => {
		const isTemplate = data?.type === INVITATION_TYPES.TEMPLATE;
		const invitationId = data?._id || "";
		const lang = data?.language === LANG_EN ? langEN : langAR;
		const fieldMap = getFieldMap(invitationId);
		const commentsSectionRef = useRef<HTMLElement>(null);
		const locationSectionRef = useRef<HTMLElement>(null);
		useImperativeHandle(ref, () => ({
			scrollToComments: () => {
				commentsSectionRef.current && commentsSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
			},
			scrollToLocation: () => {
				locationSectionRef.current && locationSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
			}
		}));

		const invitation_type_title = useMemo(() => {
			switch (data?.occasion) {
				case INVITATION_OCCASION.ENGAGEMENT: {
					return lang.the_engagement_of;
				}
				case INVITATION_OCCASION.KATB_KTAB: {
					return lang.the_katb_ktab_of;
				}
				default: {
					return lang.the_wedding_of;
				}
			}
		}, [data]);

		const invitation_getting_text = useMemo(() => {
			switch (data?.occasion) {
				case INVITATION_OCCASION.ENGAGEMENT: {
					return lang.GETTING_ENGAGED;
				}
				case INVITATION_OCCASION.KATB_KTAB: {
					return lang.GETTING_KATB_KTAB;
				}
				default: {
					return lang.GETTING_MARRIED;
				}
			}
		}, [data]);

		const invitation_location_header = useMemo(() => {
			switch (data?.occasion) {
				case INVITATION_OCCASION.ENGAGEMENT: {
					return lang.engagement_location;
				}
				case INVITATION_OCCASION.KATB_KTAB: {
					return lang.katb_ktab_location;
				}
				default: {
					return lang.wedding_location;
				}
			}
		}, [data]);

		return (
			<>
				<Wrapper id={"template_wrapper"} colormatrix={colorMatrix}>
					{/* section_01 */}
					<HeroSection id="section_01" colormatrix={colorMatrix}>
						<FloralTop>❧</FloralTop>
						<img
							src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ycAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACH5BAEAAAAALAAAAAABAAEAAAICTAEAOw=="
							alt=""
							style={{ display: "none" }}
							id={data?.fieldVisibility?.main_image?.hidden ? "main_image" : ""}
						/>
						{data?.has_images && (canEdit || !data?.fieldVisibility?.main_image?.hidden) && (
							<HeroImage className={data?.fieldVisibility?.main_image?.hidden ? "shaded" : ""}>
								<img data-dispatch-id={fieldMap.MAIN_IMAGE} src={data?.main_image} id={"main_image"} alt="Couple" />
							</HeroImage>
						)}
						<Box height={20} />
						<GoldLine />
						<Typography className={"wedding_of"} fontSize={data?.language === LANG_AR ? "25px!important" : ""}>
							{invitation_type_title}
						</Typography>
						<GoldLine />
						<Box height={15} />
						<Box display={"flex"} justifyContent={"center"}>
							<GroomName
								data-dispatch-id={fieldMap.GROOM_NAME}
								className={"guide_edit_field animate-on-scroll delay-2"}
							>
								{data?.groom_name}
							</GroomName>
						</Box>
						<Box height={10} />
						<PartnersSocialList
							socials={data?.partner_socials_1 || []}
							canEdit={canEdit}
							dispatchId={fieldMap.SOCIAL_MEDIA_1}
						/>
						<Box height={20} />
						<AndSymbol>&amp;</AndSymbol>
						<Box height={20} />
						<Box display={"flex"} justifyContent={"center"}>
							<BrideName data-dispatch-id={fieldMap.BRIDE_NAME} className={"animate-on-scroll delay-1"}>
								{data?.bride_name}
							</BrideName>
						</Box>
						<Box height={10} />
						<PartnersSocialList
							socials={data?.partner_socials_2 || []}
							canEdit={canEdit}
							dispatchId={fieldMap.SOCIAL_MEDIA_2}
						/>
						<Box height={15} />
						<GoldLine />
						<HeroDate data-dispatch-id={fieldMap.WEDDING_DATE}>
							{" "}
							{getWeddingDate(data?.wedding_date, data?.language, data?.timezone, isTemplate)}
						</HeroDate>

						<SaveTheDateBtn canEdit={canEdit} data={data!} btnText={lang.save_the_date_button_text} />
						<FloralBottom>❧</FloralBottom>
					</HeroSection>

					{/* section_02 */}
					<CountdownSection id="section_02" colormatrix={colorMatrix}>
						<CountdownTitle>{invitation_getting_text}</CountdownTitle>
						<Divider />
						<Box className={"count_down_wrapper"}>
							<CountDown
								data={data || ({} as Invitations)}
								daysString={lang.days}
								hoursString={lang.hours}
								minutesString={lang.mins}
								secondsString={lang.sec}
							/>
						</Box>
					</CountdownSection>

					{/* section_03 */}
					<IntroSection id="section_03">
						{data?.is_islamic && (
							<QuranText>بَارَكَ اللهُ لَكُما، وَبَارَكَ عَلَيْكُما، وَجَمَعَ بَيْنَكُما فِي خَيْرٍ</QuranText>
						)}
						<Divider />
						{(canEdit || !data?.fieldVisibility?.intro_text?.hidden) && (
							<>
								<MainIntro
									data-dispatch-id={fieldMap.INTRO_TEXT}
									className={`${data?.fieldVisibility?.intro_text?.hidden ? "shaded" : ""} animate-on-scroll delay-1`}
								>
									{data?.intro_text}
								</MainIntro>

								<Box height={15} />
							</>
						)}

						{(canEdit || !data?.fieldVisibility?.sub_intro_text?.hidden) && (
							<>
								<SubIntro
									data-dispatch-id={fieldMap.SUB_INTRO}
									className={` ${data?.fieldVisibility?.sub_intro_text?.hidden ? "shaded" : ""} animate-on-scroll delay-2`}
								>
									{data?.sub_intro_text}
								</SubIntro>

								<Box height={20} />
							</>
						)}

						{data?.has_images && (
							<CoupleImages colormatrix={colorMatrix}>
								<div>
									{(canEdit || !data?.fieldVisibility?.groom_image?.hidden) && (
										<img
											data-dispatch-id={fieldMap.GROOM_IMAGE}
											className={`partner_img ${data?.fieldVisibility?.groom_image?.hidden ? "shaded" : ""}`}
											src={data?.groom_image}
											alt="groom"
										/>
									)}

									<CoupleLabel
										data-dispatch-id={fieldMap.GROOM_NAME}
										style={{ fontSize: data?.language === LANG_AR ? "25px" : "" }}
									>
										{data?.groom_name}
									</CoupleLabel>
								</div>
								<div>
									{(canEdit || !data?.fieldVisibility?.bride_image?.hidden) && (
										<img
											data-dispatch-id={fieldMap.BRIDE_IMAGE}
											className={`partner_img ${data?.fieldVisibility?.bride_image?.hidden ? "shaded" : ""}`}
											src={data?.bride_image}
											alt="bride"
										/>
									)}

									<CoupleLabel
										data-dispatch-id={fieldMap.BRIDE_NAME}
										style={{ fontSize: data?.language === LANG_AR ? "25px" : "" }}
									>
										{data?.bride_name}
									</CoupleLabel>
								</div>
							</CoupleImages>
						)}
					</IntroSection>

					{/* section_04 */}
					{data?.is_islamic && (
						<QuranSection id="section_04" colormatrix={colorMatrix}>
							<Bismillah>بِسْمِ اللهِ الرَّحْمَنِ الرَّحِيمِ</Bismillah>
							<Verse>
								﴿ وَمِنْ آيَاتِهِ أَنْ خَلَقَ لَكُم مِّنْ أَنفُسِكُمْ أَزْوَاجًا لِّتَسْكُنُوا إِلَيْهَا وَجَعَلَ
								بَيْنَكُم مَّوَدَّةً وَرَحْمَةً ۚ إِنَّ فِي ذَٰلِكَ لَآيَاتٍ لِّقَوْمٍ يَتَفَكَّرُون َ﴾
							</Verse>
							<Ending>صدق الله العظيم</Ending>
						</QuranSection>
					)}

					{!data?.fieldVisibility?.loveStory?.hidden && (
						<LoveStorySection id="section_05" colormatrix={colorMatrix}>
							<SectionTitle>{lang.our_love_story}</SectionTitle>
							<Divider />
							{data?.has_images && (canEdit || !data?.fieldVisibility?.story_image?.hidden) && (
								<>
									<img
										data-dispatch-id={fieldMap.STORY_IMAGE}
										src={data?.story_image}
										className={`story_image ${data?.fieldVisibility?.story_image?.hidden ? "shaded" : ""}`}
										alt="Love Story"
									/>
									<Box height={40} />
								</>
							)}

							<StoryList data-dispatch-id={fieldMap.STORY} className={`${data?.language === LANG_AR ? RTL : ""}`}>
								{data?.story.length > 0 &&
									data?.story.map((story, index) => (
										<StoryItem
											key={story._id}
											className={`animate-on-scroll delay-${index + 1} ${data?.language === LANG_AR ? RTL : ""}`}
										>
											<StoryItemTitle>{story.title}</StoryItemTitle>
											<StoryItemContent>{story.description}</StoryItemContent>
										</StoryItem>
									))}
							</StoryList>

							{canEdit && (data?.story || []).length === 0 && (
								<Box my={3} mb={7} data-dispatch-id={fieldMap.STORY}>
									<Box className={"add_story"}>
										<AddIcon data-id={`${fieldMap.STORY}-add`} />
									</Box>
								</Box>
							)}
						</LoveStorySection>
					)}

					{/* section_06 */}
					{!data?.fieldVisibility?.gallery?.hidden && data?.has_images && (
						<GallerySection id="section_06">
							<SectionTitle>{lang.our_gallery}</SectionTitle>
							<Divider />
							<Box data-dispatch-id={fieldMap.GALLERY}>
								<InvitationGallery gallery={gallery || []} canEdit={canEdit} />
							</Box>
						</GallerySection>
					)}
					{/* section_07 */}
					{!data?.fieldVisibility?.comments?.hidden && (
						<CommentsSection id="section_07" colormatrix={colorMatrix} ref={commentsSectionRef}>
							<SectionTitle>{lang.comments}</SectionTitle>
							<Divider />
							{(canEdit || !data?.fieldVisibility?.comments_text?.hidden) && (
								<CommentTitle
									data-dispatch-id={fieldMap.COMMENTS_TEXT}
									className={`${data?.fieldVisibility?.comments_text?.hidden ? "shaded" : ""} animate-on-scroll delay-1`}
								>
									{data?.comments_text}
								</CommentTitle>
							)}
							<Box height={15} />
							{(canEdit || !data?.fieldVisibility?.comments_sub_text?.hidden) && (
								<CommentDesc
									data-dispatch-id={fieldMap.COMMENTS_SUB_TEXT}
									className={`${data?.fieldVisibility?.comments_sub_text?.hidden ? "shaded" : ""} animate-on-scroll delay-2`}
								>
									{data?.comments_sub_text}
								</CommentDesc>
							)}
							<Comments canEdit={canEdit} language={data?.language as LangType} colormatrix={colorMatrix} />
						</CommentsSection>
					)}

					{/* section_08 */}
					{!data?.fieldVisibility?.location?.hidden && (
						<LocationSection id="section_08" ref={locationSectionRef} colormatrix={colorMatrix}>
							<SectionTitle>{invitation_location_header}</SectionTitle>
							<Divider />
							{(canEdit || !data?.fieldVisibility?.wedding_location_text?.hidden) && (
								<LocationName
									data-dispatch-id={fieldMap.LOCATION_TEXT}
									className={`${data?.fieldVisibility?.wedding_location_text?.hidden ? "shaded" : ""}`}
								>
									{data?.wedding_location_text}
								</LocationName>
							)}
							<Box height={20} />

							<Box data-dispatch-id={fieldMap.MAP}>
								<MapComponent canEdit={canEdit} />
							</Box>

							<Box height={20} />
							<Box data-dispatch-id={fieldMap.MAP}>
								<MapButton canEdit={canEdit} text={lang.location} />
							</Box>
						</LocationSection>
					)}

					{/* section_09 */}
					<Footer colormatrix={colorMatrix} />
				</Wrapper>
			</>
		);
	}
);

export default Template05;
